import { OverlayView, Polygon } from '@react-google-maps/api';
import Map from 'components/Map';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Action } from 'redux';
import { LocationType } from 'types';
import { Close } from 'components/Icons';

import styles from './styles.module.scss';

const polygonOptions: google.maps.PolygonOptions = {
  strokeColor: '#252D44',
  fillColor: '#252D44',
  fillOpacity: 0.08,
};

interface MapWithDrawingManagerProps {
  disableDraw: boolean;
  showRemoveBoundaryBtn: boolean;
  getDrawnPolygonSelectorFn: (state: any) => object;
  getIsMapDrawingModeSelectorFn: (state: any) => boolean;
  toggleMapDrawingModeDispatchFn: (newValue: boolean) => Action<unknown>;
  setDrawnPolygonDispatchFn: (polygon: object) => Action<unknown>;
  onBoundsChangedHandlerFn?: (viewportCoordinates, isInitialViewportCoordinate) => void;
  listingCountTextForMap: string;
  isMapEnhancementEnabled: boolean;
  selectedLocations: string[];
  polygonLocations: any;
  markersMap: any;
}

export const MapWithDrawingManager: React.FC<MapWithDrawingManagerProps> = (props) => {
  const {
    polygonLocations: polygonLocations_,
    getIsMapDrawingModeSelectorFn,
    setDrawnPolygonDispatchFn,
    disableDraw,
    showRemoveBoundaryBtn,
    markersMap,
  } = props;
  const [polygonLocations, setPolygonLocations] = useState(polygonLocations_ || []);

  const isDrawingMode = useSelector(getIsMapDrawingModeSelectorFn);

  const [polygon, setPolygon] = useState<google.maps.Polygon | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const isDrawingRef = useRef(false);
  const [hoverPolygonIndex, setHoverPolygonIndex] = useState<any>(null);

  useEffect(() => {
    setPolygonLocations(polygonLocations_);
  }, [polygonLocations_]);

  const handleMouseMove = useCallback((e: google.maps.MapMouseEvent) => {
    if (!isDrawingRef.current || !e.latLng || !polylineRef.current) return;
    polylineRef.current.getPath().push(e.latLng);
  }, []);

  const onShapeDraw = async (polyline: google.maps.Polyline) => {
    // Convert Polyline to Polygon for compatibility
    const path = polyline.getPath().getArray();

    if (path.length < 3) {
      polyline.setMap(null);
      return;
    }

    // Close the shape
    path.push(path[0]);

    const polygon = new google.maps.Polygon({
      paths: path,
      map: polyline.getMap(),
    });

    setPolygon(polygon);
    const geojsonPolygon: any = await gmapsPolygonToGeoJson(polygon);
    if (geojsonPolygon?.coordinates?.[0]?.length < 4) {
      polygon.setMap(null);
      return;
    }

    const newPolygon = {
      Type: LocationType.Polygon,
      Polygon: geojsonPolygon,
    };

    setPolygonLocations((prev) => {
      const locations = [...prev, newPolygon];
      setDrawnPolygonDispatchFn(locations);
      return locations;
    });

    polyline.setMap(null);
  };

  const stopDrawing = useCallback(() => {
    if (!isDrawingRef.current) return;

    isDrawingRef.current = false;
    if (polylineRef.current) {
      const path = polylineRef.current.getPath();
      if (path.getLength() > 2) {
        path.push(path.getAt(0)); // close shape
      }
      onShapeDraw?.(polylineRef.current);
    }

    window.removeEventListener('mouseup', stopDrawing);
  }, [onShapeDraw]);

  // Dynamically update gestureHandling
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setOptions({
        gestureHandling: isDrawingMode ? 'none' : 'auto',
      });
    }
  }, [isDrawingMode]);

  const handleLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;

      map.addListener('mousedown', (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;

        isDrawingRef.current = true;

        const polyline = new google.maps.Polyline({
          strokeColor: '#000000',
          strokeOpacity: 1,
          strokeWeight: 2.5,
          map,
          path: [e.latLng],
        });

        polyline.setMap(map);
        polylineRef.current = polyline;
        map.addListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', stopDrawing);
      });
    },
    [handleMouseMove, stopDrawing, isDrawingMode],
  );

  // Clean up on unmount
  useEffect(() => {
    return () => {
      window.removeEventListener('mouseup', stopDrawing);
    };
  }, [stopDrawing]);

  useEffect(() => {
    if (polygon) {
      polygon.setMap(null);
    }
    if (polygonLocations?.length && !isDrawingMode) {
      setPolygon(geoJsonPolygonToGmaps(polygonLocations?.[0]?.Polygon));
    }
  }, [polygonLocations]);

  useEffect(() => {
    if (isDrawingMode) {
      if (polygon) {
        polygon.setMap(null);
      }
      setPolygon(null);
    }
  }, [isDrawingMode]);

  const handleRemoveBoundary = () => {
    if (hoverPolygonIndex === null) return;
    const newLocations = [...polygonLocations];
    newLocations.splice(hoverPolygonIndex, 1);
    setPolygonLocations(newLocations);
    setHoverPolygonIndex(null);
    setDrawnPolygonDispatchFn(newLocations);
  };

  return (
    <Map
      {...props}
      markersMap={isDrawingMode ? [] : markersMap}
      noFitBounds
      callBackOnLoad={handleLoad}
    >
      {polygonLocations?.length && !disableDraw ? (
        <Polygon
          paths={polygonLocations.map(({ Polygon }) => geoJsonPolygonToGmaps(Polygon).getPath())}
          options={polygonOptions}
        />
      ) : null}
      {polygonLocations?.length &&
        polygonLocations?.map(({ Polygon: poly }, index) => {
          const path = geoJsonPolygonToGmaps(poly).getPath();
          const firstLatLng = path.getLength() > 0 ? path.getAt(0) : null;
          return (
            <>
              <Polygon
                key={index}
                path={path}
                onMouseOver={() => setHoverPolygonIndex(index)}
                onMouseOut={() => setHoverPolygonIndex(null)}
                options={{
                  ...polygonOptions,
                  ...(showRemoveBoundaryBtn
                    ? { fillOpacity: hoverPolygonIndex === index ? 0.5 : 0.2 }
                    : {}), // highlight effect
                }}
              />
              {showRemoveBoundaryBtn && hoverPolygonIndex === index && firstLatLng && (
                <OverlayView position={firstLatLng} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                  <div
                    className={styles.removeBoundaryBtn}
                    onClick={handleRemoveBoundary} // handler for removing the polygon
                  >
                    <Close color="#fff" className={styles.closeIcon} />
                    <span>Remove Boundary</span>
                  </div>
                </OverlayView>
              )}
            </>
          );
        })}
    </Map>
  );
};

async function gmapsPolygonToGeoJson(polygon: google.maps.Polygon): Promise<object> {
  var dataLayer = new window.google.maps.Data();
  dataLayer.add(
    new window.google.maps.Data.Feature({
      geometry: new window.google.maps.Data.Polygon([polygon.getPath().getArray()]),
    }),
  );

  return new Promise((resolve) => {
    dataLayer.toGeoJson((geoJsonValue) => {
      const firstFeature = (geoJsonValue as any).features[0];
      const geojsonPolygon = {
        type: 'Polygon',
        coordinates: firstFeature.geometry.coordinates,
      };
      resolve(geojsonPolygon);
    });
  });
}

function geoJsonPolygonToGmaps(geoJsonPolygon: any): google.maps.Polygon {
  const gmapsPolygon = new google.maps.Polygon();
  const coordinates = geoJsonPolygon.coordinates[0]
    .slice(0, -1) // remove the last point which is the same as the first in geojson
    .map(([lng, lat]) => new google.maps.LatLng(lat, lng));
  gmapsPolygon.setPaths(coordinates);
  return gmapsPolygon;
}
